import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Crown, ArrowRight, Check } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent } from './dialog';
import { useLanguage } from '../../contexts/LanguageContext';

const PREMIUM_FEATURES = {
  en: {
    'Employee & Payroll Management': [
      'Employee records & NID management',
      'Monthly salary & advance tracking',
      'Payroll reports & payment history',
    ],
    'Profit Reports': [
      'Profit & loss breakdown by period',
      'Expense tracking & categorization',
      'Profit trends & charts',
    ],
    'Jobs Analytics': [
      'Job completion analytics',
      'Technician performance tracking',
      'Job trends over time',
    ],
    'Payments Report': [
      'Payment method breakdown',
      'Collection trends & charts',
      'Due payment tracking',
    ],
    'Loyalty Points': [
      'Customer loyalty point system',
      'Automatic point earning on invoices',
      'Point redemption & discount',
    ],
    'Referral Program': [
      'Customer referral tracking',
      'Referral reward management',
      'New customer acquisition insights',
    ],
    'SMS Campaigns': [
      'Bulk SMS to customers',
      'Campaign scheduling',
      'Campaign analytics & delivery tracking',
    ],
    'Smart Reminders': [
      'Automated service reminders',
      'Custom reminder scheduling',
      'SMS-based customer follow-ups',
    ],
    'SMS Credits': [
      'SMS credit balance & top-up',
      'Usage history & tracking',
      'Credit purchase management',
    ],
    'Thermal Print': [
      'Thermal receipt printing (58mm/80mm)',
      'Quick POS-style invoice prints',
      'Compact quotation printing',
    ],
    'Split Payment': [
      'Split payment across methods (Cash + bKash)',
      'Multi-method payment recording',
      'Flexible payment collection',
    ],
    'AI Smart Import': [
      'Scan supplier bills with AI',
      'Auto-detect parts, prices & quantities',
      'Bulk import from PDF, photo, Excel',
    ],
    'Marketing Impact': [
      'Campaign ROI & delivery analytics',
      'Loyalty & referral performance tracking',
      'Customer retention insights',
    ],
  },
  bn: {
    'কর্মচারী ও বেতন ব্যবস্থাপনা': [
      'কর্মচারী রেকর্ড ও NID ব্যবস্থাপনা',
      'মাসিক বেতন ও অগ্রিম ট্র্যাকিং',
      'বেতন রিপোর্ট ও পেমেন্ট ইতিহাস',
    ],
    'লাভ-ক্ষতি রিপোর্ট': [
      'পিরিয়ড অনুযায়ী লাভ-ক্ষতি বিশ্লেষণ',
      'খরচ ট্র্যাকিং ও শ্রেণীবিভাগ',
      'লাভের ট্রেন্ড ও চার্ট',
    ],
    'জব বিশ্লেষণ': [
      'জব সম্পন্নের বিশ্লেষণ',
      'টেকনিশিয়ান পারফরম্যান্স ট্র্যাকিং',
      'সময়ের সাথে জবের ট্রেন্ড',
    ],
    'পেমেন্ট রিপোর্ট': [
      'পেমেন্ট পদ্ধতির বিশ্লেষণ',
      'কালেকশন ট্রেন্ড ও চার্ট',
      'বকেয়া পেমেন্ট ট্র্যাকিং',
    ],
    'লয়াল্টি পয়েন্ট': [
      'কাস্টমার লয়াল্টি পয়েন্ট সিস্টেম',
      'ইনভয়েসে স্বয়ংক্রিয় পয়েন্ট অর্জন',
      'পয়েন্ট রিডিম ও ডিসকাউন্ট',
    ],
    'রেফারেল প্রোগ্রাম': [
      'কাস্টমার রেফারেল ট্র্যাকিং',
      'রেফারেল পুরস্কার ব্যবস্থাপনা',
      'নতুন কাস্টমার অর্জনের তথ্য',
    ],
    'এসএমএস ক্যাম্পেইন': [
      'কাস্টমারদের বাল্ক এসএমএস',
      'ক্যাম্পেইন শিডিউলিং',
      'ক্যাম্পেইন বিশ্লেষণ ও ডেলিভারি ট্র্যাকিং',
    ],
    'স্মার্ট রিমাইন্ডার': [
      'স্বয়ংক্রিয় সার্ভিস রিমাইন্ডার',
      'কাস্টম রিমাইন্ডার শিডিউলিং',
      'এসএমএস-ভিত্তিক কাস্টমার ফলো-আপ',
    ],
    'এসএমএস ক্রেডিট': [
      'এসএমএস ক্রেডিট ব্যালেন্স ও টপ-আপ',
      'ব্যবহারের ইতিহাস ও ট্র্যাকিং',
      'ক্রেডিট ক্রয় ব্যবস্থাপনা',
    ],
    'থার্মাল প্রিন্ট': [
      'থার্মাল রিসিট প্রিন্টিং (৫৮mm/৮০mm)',
      'দ্রুত POS-স্টাইল ইনভয়েস প্রিন্ট',
      'কম্প্যাক্ট কোটেশন প্রিন্টিং',
    ],
    'বিভক্ত পেমেন্ট': [
      'একাধিক পদ্ধতিতে পেমেন্ট বিভক্ত (নগদ + বিকাশ)',
      'মাল্টি-মেথড পেমেন্ট রেকর্ডিং',
      'নমনীয় পেমেন্ট কালেকশন',
    ],
    'AI স্মার্ট ইমপোর্ট': [
      'AI দিয়ে সাপ্লায়ারের বিল স্ক্যান',
      'পার্ট, দাম ও পরিমাণ স্বয়ংক্রিয় সনাক্তকরণ',
      'PDF, ছবি, Excel থেকে বাল্ক ইমপোর্ট',
    ],
    'মার্কেটিং ইমপ্যাক্ট': [
      'ক্যাম্পেইন ROI ও ডেলিভারি বিশ্লেষণ',
      'লয়াল্টি ও রেফারেল পারফরম্যান্স ট্র্যাকিং',
      'কাস্টমার রিটেনশন ইনসাইটস',
    ],
  },
};

export function PremiumOverlay({ feature, children, locked = true }) {
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const { language } = useLanguage();

  if (!locked) return children;

  const handleUpgrade = () => {
    navigate(`/${garageSlug}/admin/settings?tab=subscription`);
  };

  const features = PREMIUM_FEATURES[language]?.[feature] || [];

  return (
    <div className="relative min-h-[400px]">
      {/* Blurred content */}
      <div className="pointer-events-none select-none" style={{ filter: 'blur(5px)', opacity: 0.5 }}>
        {children}
      </div>
      {/* Overlay card - absolute, viewport-height so card centers in visible area */}
      <div
        className="absolute inset-x-0 top-0 z-10 pointer-events-none flex items-center justify-center"
        style={{ height: 'calc(100vh - 300px)' }}
      >
        <div className="pointer-events-auto">
          <div className="bg-white/95 backdrop-blur-sm border border-emerald-200 rounded-xl shadow-lg px-8 py-6 max-w-sm text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Crown className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-base font-semibold text-slate-800 mb-1">
              {feature}
            </h3>
            <p className="text-sm text-slate-500 mb-3">
              {language === 'en'
                ? 'Upgrade to Premium to unlock this feature'
                : 'এই ফিচারটি আনলক করতে প্রিমিয়ামে আপগ্রেড করুন'}
            </p>
            {features.length > 0 && (
              <ul className="text-left text-sm text-slate-600 space-y-1.5 mb-4">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            <Button
              onClick={handleUpgrade}
              className="bg-gradient-to-r from-emerald-700 to-emerald-500 hover:from-emerald-800 hover:to-emerald-600 text-white gap-2 w-full"
              size="sm"
            >
              {language === 'en' ? 'Upgrade to Premium' : 'প্রিমিয়ামে আপগ্রেড'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Standalone upgrade popup dialog for action-level blocks (thermal print, split payment, etc.)
export function PremiumUpgradeDialog({ open, onOpenChange, feature }) {
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const { language } = useLanguage();

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate(`/${garageSlug}/admin/settings?tab=subscription`);
  };

  const features = PREMIUM_FEATURES[language]?.[feature] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Crown className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-1">
            {feature}
          </h3>
          <p className="text-sm text-slate-500 mb-3">
            {language === 'en'
              ? 'This is a Premium feature. Upgrade to unlock it.'
              : 'এটি একটি প্রিমিয়াম ফিচার। আনলক করতে আপগ্রেড করুন।'}
          </p>
          {features.length > 0 && (
            <ul className="text-left text-sm text-slate-600 space-y-1.5 mb-4">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
          <Button
            onClick={handleUpgrade}
            className="bg-gradient-to-r from-emerald-700 to-emerald-500 hover:from-emerald-800 hover:to-emerald-600 text-white gap-2 w-full"
            size="sm"
          >
            {language === 'en' ? 'Upgrade to Premium' : 'প্রিমিয়ামে আপগ্রেড'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
